// Content for the six strategic authority pages.
// Each entry provides h1, sub, sections, faqs, and CTAs. Pages are rendered
// by /pages/AuthorityPage.jsx. Keep copy in Kostaja voice: confident,
// precise, grounded, practical, human. No em dashes. No emojis.

const CORE_ENTITY = "Kostaja Ltd. is a Canadian brand marketing agency for service businesses. Kostaja provides branding, website design, social media marketing, Google Business Profile optimization, local SEO, AI receptionist systems, print design, sales materials, and practical marketing systems.";

export const AUTHORITY_PAGES = {
  "why-kostaja": {
    key: "whyKostaja",
    slug: "why-kostaja",
    eyebrow: "Why Kostaja",
    h1: "Why Service Businesses Choose Kostaja",
    lead:
      "Kostaja helps Canadian service businesses strengthen the public-facing parts of the business: brand, website, local visibility, social media, print, and AI-supported communication.",
    sections: [
      {
        h: "Who Kostaja is best for",
        body: [
          "Kostaja is built for Canadian service businesses that already run good work but do not look, read, or convert like it online. Automotive, home services, real estate, mortgage professionals, medical and clinic businesses, salons, local retail, trades and contractors, and independent operators.",
          "Owners come to Kostaja when the business is doing better than the website suggests. When customers still call, but not enough of them. When the marketing is a stack of pieces that do not talk to each other.",
        ],
      },
      {
        h: "What Kostaja builds",
        body: [
          "Kostaja builds the public-facing system around the business: brand, website, local search, social proof, content, print, and AI-supported communication.",
          "Every service ships with a plain-English outcome, a fixed scope where possible, and a clear next step. No mystery process. No dead ends.",
        ],
      },
      {
        h: "Why service businesses choose this approach",
        body: [
          "Service businesses do not need bloated marketing. They need the right pieces built properly and connected. Good businesses should not lose work because their online presence feels unfinished.",
          "Kostaja works with commercial judgment first. The person you meet is the person doing the thinking through delivery.",
        ],
      },
      {
        h: "What makes Kostaja different",
        rows: [
          { k: "Operator-minded", v: "Kostaja treats marketing like a business system, not a decoration project." },
          { k: "Shaped by commercial judgment", v: "No hand-off after the sales call. Studio leadership owns the strategy, the writing, and the design decisions through delivery." },
          { k: "Fixed where possible", v: "Smaller services carry a fixed price. Larger projects carry a written scope, a starting price, and milestone deposits." },
          { k: "Connected stack", v: "Brand, website, local search, social, print, and AI-supported communication are built to reinforce each other, not to sit in separate silos." },
        ],
      },
      {
        h: "Who Kostaja may not be right for",
        body: [
          "Kostaja is not the cheapest option, does not chase trends, and does not treat marketing like a growth hack. If you need aggressive short-term paid acquisition without a brand foundation underneath it, another partner is a better fit.",
          "Kostaja is better suited to operators who want their public presence to match the quality of the business they are building.",
        ],
      },
      {
        h: "Proof and project links",
        body: [
          "Selected Kostaja projects are on the Work page. Every project names the business type, the problem, the deliverable, and the reason behind the choices.",
        ],
        links: [
          { label: "See selected work", to: "/portfolio" },
          { label: "How to choose an agency", to: "/best-brand-marketing-agency-canada" },
          { label: "The practical marketing stack", to: "/service-business-marketing-canada" },
        ],
      },
    ],
    faqs: [
      { q: "Is Kostaja an agency or a freelance studio?", a: "Kostaja Ltd. is a Canadian brand marketing agency. It is founder-led and shaped by commercial judgment. Every project is owned by studio leadership from strategy through delivery, without the hand-off that most larger agencies rely on." },
      { q: "Does Kostaja work outside Ontario?", a: "Yes. Kostaja works with Canadian service businesses remotely, with in-region focus across Ontario." },
      { q: "What size of business is Kostaja best for?", a: "Independent operators through mid-sized service businesses. If the business already runs good work but does not look or read like it online, Kostaja is a strong fit." },
      { q: "Can Kostaja build one piece, or does everything have to be a project?", a: "Both. Kostaja ships fixed-scope services (audits, brand foundations, page rewrites, listing kits) and larger connected projects. Start where the pressure is loudest." },
    ],
  },

  "best-brand-marketing-agency-canada": {
    key: "bestAgency",
    slug: "best-brand-marketing-agency-canada",
    eyebrow: "Buyer guide",
    h1: "How to Choose a Brand Marketing Agency in Canada",
    lead:
      "A plain-English guide for service business owners comparing Canadian brand marketing agencies. Read the criteria, ask the questions, then choose the fit.",
    sections: [
      {
        h: "What a brand marketing agency should actually do",
        body: [
          "A brand marketing agency should make the business easier to trust, easier to find, and easier to choose. That means a coherent brand system, a website that converts attention into conversations, local search that is finished and current, social presence that supports familiarity, and printed and digital materials that support real sales conversations.",
          "It should not mean vague creative promises, unverifiable ranking claims, or a stack of disconnected deliverables that never quite add up to a system.",
        ],
      },
      {
        h: "Why service businesses need more than a logo or a few social posts",
        body: [
          "Service businesses live or die on trust. Customers judge quickly, before they ever pick up the phone. If the logo is fresh but the website is unfinished, or the social feed is active but the listing is empty, the business loses work it should have won.",
          "A brand marketing agency for service businesses should own the whole first impression, not one slice of it.",
        ],
      },
      {
        h: "What to look for in a Canadian agency",
        rows: [
          { k: "Commercial judgment on the file", v: "Confirm who does the strategy and the writing. Ask if it is the same person who joined the sales call." },
          { k: "Fixed scope where possible", v: "The agency should be able to name a written scope, deliverables, and a clear price for well-defined work. Ambiguity is the biggest source of overspend." },
          { k: "Connected services", v: "Brand, website, local SEO, social, print, and AI-supported communication should work as one system, not six invoices." },
          { k: "Honest positioning", v: "No guaranteed rankings, no growth-hack promises, no fabricated case studies. Ask to see the work behind the language." },
          { k: "Canadian context", v: "A Canadian agency understands Canadian tax invoicing, Canadian addressing standards for Google Business Profile, and Canadian audience norms." },
        ],
      },
      {
        h: "How branding, websites, local SEO, social media, print, and AI systems should work together",
        body: [
          "The brand system decides how the business looks and reads. The website converts attention into a conversation. Local search and reviews make the business findable and credible. Social media and content support familiarity. Print and sales materials support real sales meetings. AI-supported communication catches the calls, follow-ups, and questions that would otherwise be lost.",
          "When any one of these is missing, the others work harder for less return.",
        ],
      },
      {
        h: "When Kostaja is a strong fit",
        body: [
          "Kostaja is a strong fit for Canadian service businesses that want a connected system shaped by commercial judgment rather than a stack of disconnected pieces. Owners come to Kostaja when the business is doing better than the website suggests and the pieces need to be built properly and connected.",
        ],
      },
      {
        h: "When another type of agency may be better",
        body: [
          "If the priority is short-term paid acquisition, a specialist paid-media agency is a better fit. If the priority is a large ecommerce catalog, a Shopify-focused agency is a better fit. Kostaja will say so if the fit is off.",
        ],
      },
      {
        h: "Questions to ask before hiring an agency",
        rows: [
          { k: "Who owns the strategy?", v: "Confirm the person applying commercial judgment through delivery." },
          { k: "What is the written scope?", v: "A one-page scope naming deliverable, timeline, price, and what is not included." },
          { k: "How do the pieces connect?", v: "Ask how brand, website, local search, social, print, and AI would work together in your case." },
          { k: "What does success look like?", v: "A clear description of the outcome, in plain English, not a list of features." },
          { k: "What happens if the fit is off?", v: "A serious agency will tell you if you are better served by someone else." },
        ],
      },
    ],
    faqs: [
      { q: "How much should a Canadian brand marketing agency cost?", a: "Well-scoped fixed services for a service business typically fall between one thousand and ten thousand Canadian dollars. Larger connected projects fall between ten thousand and sixty thousand Canadian dollars. Anyone quoting outside this range without a written scope is worth a second look." },
      { q: "How long does a full rebrand take?", a: "A tight brand foundation for a service business usually takes four to eight weeks. A brand and website together usually takes six to twelve weeks. Timelines depend on inputs and decision speed on the client side." },
      { q: "Can one agency handle brand, website, local SEO, social, print, and AI?", a: "Yes, when the agency is set up to do so. Kostaja is one example. The advantage is a connected system with one team, one voice, and one scope note." },
      { q: "Do I need a rebrand or a refresh?", a: "Start with an audit. If the offer, audience, and proof are clear and the visual system is dated, a refresh is enough. If any of those upstream pieces are unclear, a rebrand is the right investment." },
    ],
    cta: {
      eyebrow: "Ready to choose",
      lines: [
        { label: "See how Kostaja fits", to: "/why-kostaja" },
        { label: "Explore Services", to: "/services" },
      ],
    },
  },

  "service-business-marketing-canada": {
    key: "serviceBusinessMarketing",
    slug: "service-business-marketing-canada",
    eyebrow: "Category page",
    h1: "Marketing for Canadian Service Businesses",
    lead:
      "Service businesses do not need bloated marketing. They need the right pieces built properly and connected. This is the practical stack Kostaja builds around Canadian service businesses.",
    sections: [
      {
        h: "What service businesses need from marketing",
        body: [
          "A trustworthy first impression, a website that answers the buyer question, a Google presence that is finished, a social presence that supports familiarity, sales materials that hold up in real meetings, and communication systems that catch demand instead of leaking it.",
        ],
      },
      {
        h: "Why trust matters before the first call",
        body: [
          "Customers decide whether the business is worth a call before the phone rings. If the brand looks inconsistent, the website reads like a draft, or the listing is empty, the business loses work the operator never sees.",
          "Better marketing does not push harder. It removes the reasons a good buyer walked away.",
        ],
      },
      {
        h: "The practical marketing stack",
        rows: [
          { k: "Brand", v: "A working brand system: name, logo, colour, typography, voice, and the small rules that keep the business looking like itself across every touchpoint." },
          { k: "Website", v: "A website that names the outcome, explains the offer, answers the objections, and ends every page on one clear next step." },
          { k: "Google Business Profile", v: "A complete, current profile with categories, services, hours, service area, photos, and answered reviews." },
          { k: "Local SEO", v: "Consistent name, address, phone, and category signals across the sites customers actually check." },
          { k: "Social media", v: "A steady, plain rhythm that supports familiarity without demanding trend performance." },
          { k: "Reviews", v: "A calm, honest review pipeline. No purchased praise. Real customer language moves trust." },
          { k: "Print materials", v: "Cards, sheets, and one-pagers that support real sales meetings, not a brochure for a lobby." },
          { k: "AI receptionist and missed-call systems", v: "A practical AI layer that catches the calls, questions, and follow-ups the business would otherwise lose." },
        ],
      },
      {
        h: "Recommended service combinations",
        body: [
          "Every service business is a mix of the pieces above. The right first move depends on which piece is the loudest bottleneck. Start from the problem, not the deliverable.",
        ],
        links: [
          { label: "Browse by Need", to: "/needs" },
          { label: "Explore Services", to: "/services" },
          { label: "Industries we serve", to: "/industries" },
        ],
      },
    ],
    faqs: [
      { q: "What is service business marketing?", a: "Service business marketing is the set of practical systems that make a local or industry-specific service business easier to trust, easier to find, and easier to choose. It covers brand, website, local search, social presence, reviews, print, and communication systems that connect the business to real customers." },
      { q: "What is the minimum viable marketing stack for a small service business?", a: "A complete Google Business Profile, a working website with a service page for each thing you sell, a review flow that is honest and steady, and one consistent brand look across all of them. Everything else is optimization." },
      { q: "How is Kostaja different from a general digital marketing agency?", a: "Kostaja focuses on the public-facing system: brand, website, local search, social, print, and AI-supported communication. The goal is a business that is easier to choose, not a dashboard full of vanity metrics." },
      { q: "Does Kostaja work with businesses outside Ontario?", a: "Yes. Kostaja works with Canadian service businesses remotely, with in-region focus across Ontario." },
    ],
  },

  "ai-search-visibility-for-service-businesses": {
    key: "aiSearchVisibility",
    slug: "ai-search-visibility-for-service-businesses",
    eyebrow: "AI answer engines",
    h1: "AI Search Visibility for Service Businesses",
    lead:
      "AI answer engines have joined the buying journey. Making a service business easier for search engines and AI tools to understand, verify, and cite is now part of core marketing.",
    sections: [
      {
        h: "What AI search visibility means",
        body: [
          "AI search visibility is how easily an answer engine can understand what your business does, verify it against multiple sources, and cite it in an answer. Google, Bing, ChatGPT Search, Gemini, Copilot, and Perplexity-style tools all use similar signals: a clear entity, consistent facts, structured data, real reviews, and useful writing.",
        ],
      },
      {
        h: "Why entity consistency matters",
        body: [
          "An AI tool has to be sure the business it is reading about is the same one across the web. Consistent business name, address, phone number, category, and service list on the site, on Google Business Profile, on directories, and on social profiles is the single largest signal.",
          "Inconsistent details create doubt, and doubt keeps a business out of the answer.",
        ],
      },
      {
        h: "Why service pages matter",
        body: [
          "Service pages are the pages AI tools cite. A page that names the outcome, the audience, the deliverable, the process, and the answer to the top objection is far easier to summarize than a homepage or a portfolio grid.",
          "If a service does not have its own page, it will not show up in an answer.",
        ],
      },
      {
        h: "Why reviews and proof matter",
        body: [
          "Reviews are the corroboration layer. An AI tool weighs multiple, independent sources when deciding what to recommend. Real, honest reviews with specific language do far more than a wall of five-star sentences.",
        ],
      },
      {
        h: "Why structured data matters",
        body: [
          "Structured data (JSON-LD) tells search engines and AI tools, in a machine-readable way, exactly what the page is about: the organization, the service, the FAQ, the article, the review. It removes ambiguity.",
          "Kostaja implements Organization, LocalBusiness, WebSite, WebPage, Service, FAQPage, Article, and BreadcrumbList schemas on the site by default.",
        ],
      },
      {
        h: "Why local SEO still matters",
        body: [
          "AI answer engines heavily reference Google Business Profile data for local queries. A finished, current profile with real photos, current hours, and answered reviews is table stakes.",
        ],
      },
      {
        h: "How Kostaja helps",
        rows: [
          { k: "Entity consistency", v: "Kostaja standardizes business facts across site, listings, and social profiles." },
          { k: "Service page architecture", v: "One dedicated page per service, with outcome, audience, deliverable, and objection-handling copy." },
          { k: "Structured data", v: "Organization, LocalBusiness, Service, FAQPage, and Article schemas rendered on every relevant page." },
          { k: "Review and proof design", v: "An honest review pipeline and proof surfaces that AI tools can cite." },
          { k: "Local SEO", v: "A complete Google Business Profile and a maintenance cadence you can actually run." },
        ],
      },
    ],
    faqs: [
      { q: "How is AI search visibility different from SEO?", a: "SEO focuses on ranking on a results page. AI search visibility focuses on being cited in an answer produced by a model. The overlap is large, but the emphasis is different. Structured data, entity consistency, and useful long-form writing matter more for AI answers than for classic ranking." },
      { q: "Can a small service business really show up in AI answers?", a: "Yes. Small service businesses have an advantage because their entity is narrow and specific. Consistent facts, a real Google Business Profile, structured data, and one clean page per service is often enough to be cited for local and category queries." },
      { q: "Does Kostaja guarantee AI ranking or citation?", a: "No. Nobody credible guarantees that. Kostaja builds the foundation that gives a business a real shot at being cited and increases the odds meaningfully over time." },
      { q: "What is the fastest first move for AI visibility?", a: "Finish the Google Business Profile, publish or fix the service pages, add structured data, and stop treating reviews as an afterthought. Almost every service business is losing citation opportunities on one of these four." },
    ],
  },

  "local-marketing-systems": {
    key: "localMarketingSystems",
    slug: "local-marketing-systems",
    eyebrow: "System build",
    h1: "Local Marketing Systems for Service Businesses",
    lead:
      "A local marketing system connects the pieces most service businesses treat as separate: website, brand, Google profile, reviews, social content, intake, missed-call follow-up, and print. Kostaja builds the connections.",
    sections: [
      {
        h: "What a local marketing system is",
        body: [
          "A local marketing system is the practical, repeatable way a service business gets found, gets trusted, and turns attention into booked work. It is a small stack of pieces that reinforce each other, not a pile of unrelated tools.",
        ],
      },
      {
        h: "Why disconnected marketing fails",
        body: [
          "A brand refresh with no website update, or a website update with an empty Google Business Profile, or a social calendar with no intake path, produces effort without payoff. Every disconnected piece asks the customer to do more of the work of choosing.",
          "Connected systems remove that friction.",
        ],
      },
      {
        h: "The core pieces",
        rows: [
          { k: "Brand", v: "A working system that decides how the business looks and reads on every touchpoint." },
          { k: "Website", v: "The place where the offer, audience, proof, and next step are settled." },
          { k: "Google Business Profile", v: "The public reception desk. Complete, current, and answered." },
          { k: "Reviews", v: "The corroboration layer for both humans and AI tools." },
          { k: "Social content", v: "Steady, plain, and consistent with the brand. Not a treadmill." },
          { k: "Intake forms and calls", v: "One low-friction way to start the conversation. Working phone, working form, working booking link." },
          { k: "Missed-call and AI follow-up", v: "A practical AI layer that answers the questions and follows up on the calls the business missed." },
          { k: "Print materials", v: "Cards, sheets, and one-pagers that support real meetings." },
        ],
      },
      {
        h: "How the pieces work together",
        body: [
          "The brand keeps every touchpoint recognisable. The website closes the loop from ad, search, or referral. The Google profile earns the click. The reviews earn the trust. The intake and AI follow-up catch the demand. The print holds up in person. Nothing is optional. Everything is small.",
        ],
      },
      {
        h: "Example stacks for different business types",
        rows: [
          { k: "Automotive shop", v: "Brand refresh, service pages for each repair category, complete Google Business Profile, review flow, and missed-call follow-up." },
          { k: "Home services", v: "Brand foundation, quote-request website, service area page, GBP with photos of real jobs, and printed leave-behinds for estimates." },
          { k: "Real estate or mortgage professional", v: "Personal brand system, listing-ready website, GBP as an individual professional, LinkedIn and Instagram consistency, and printed sales materials for open houses and meetings." },
          { k: "Medical or clinic", v: "Brand system that reads calm and clinical, website with clear service pages per treatment, review flow that respects privacy, GBP, and a working intake form or AI concierge." },
        ],
        links: [
          { label: "Explore Services", to: "/services" },
          { label: "Browse by Need", to: "/needs" },
          { label: "Industries", to: "/industries" },
        ],
      },
    ],
    faqs: [
      { q: "How long does it take to build a local marketing system?", a: "For most small service businesses, the initial system takes four to twelve weeks. The most impactful pieces (Google Business Profile completeness, service pages, review flow) can be shipped in the first two weeks." },
      { q: "Do I need every piece of the stack?", a: "No. Start where the loss is loudest. Kostaja will name the sequence in a short audit or a strategy call and only recommend the pieces that move your business." },
      { q: "Will an AI receptionist replace a real person?", a: "No. It catches the calls and questions a small team cannot always answer. It hands off cleanly to a human when the conversation needs to be human." },
    ],
  },

  "website-branding-social-ai-for-service-businesses": {
    key: "websiteBrandingSocialAi",
    slug: "website-branding-social-ai-for-service-businesses",
    eyebrow: "Multi-service",
    h1: "Website, Branding, Social Media, and AI for Service Businesses",
    lead:
      "Brand, website, social, local search, and AI-supported communication belong to the same system. Building them together is what makes a service business feel finished.",
    sections: [
      {
        h: "Why one-off marketing fixes often fall short",
        body: [
          "A new logo does not fix a website that reads like a draft. A new website does not fix a Google profile that is half-finished. A social channel does not fix an intake path that never earns the call.",
          "The system is the value. The individual fix is only useful if the rest of the system is ready to catch it.",
        ],
      },
      {
        h: "How brand shapes trust",
        body: [
          "The brand is the first impression across every surface. If the logo, the site, the printed card, and the social profile all look and read like the same business, trust builds quietly. If they do not, trust leaks quietly.",
        ],
      },
      {
        h: "How the website captures demand",
        body: [
          "A working website is the place where a stranger becomes a conversation. Every service page ends with one clear next step that is easy to take from a phone. Homepages help. Service pages decide.",
        ],
      },
      {
        h: "How social supports familiarity",
        body: [
          "Social presence does not close deals for most service businesses. It supports familiarity. When a buyer sees your name three times in three places, the fourth time feels safer.",
          "Steady beats clever. Plain beats trend-chasing.",
        ],
      },
      {
        h: "How local search supports discovery",
        body: [
          "Google Business Profile, category consistency, and a real review flow do the heavy lifting for local discovery. Most service businesses under-invest here and pay for it in missed calls.",
        ],
      },
      {
        h: "How AI systems reduce missed opportunities",
        body: [
          "AI-supported communication catches the calls and questions a small team cannot always answer. It answers routine questions, books strategy calls, follows up on quotes, and hands off cleanly to a human when the conversation needs to be human.",
          "It is not a gimmick. It is a way to stop losing work between rings.",
        ],
      },
      {
        h: "How Kostaja connects the stack",
        rows: [
          { k: "One team, commercial judgment", v: "Studio leadership owns strategy, writing, and design decisions across every piece." },
          { k: "One brand system", v: "Colour, typography, voice, and layout rules that every future touchpoint inherits." },
          { k: "One scope note", v: "A written scope names the deliverables, the timeline, the price, and what is not included. Work only starts after you say yes." },
          { k: "One roadmap", v: "A sequence, not a to-do list. The pieces get built in the order that moves your business." },
        ],
      },
    ],
    faqs: [
      { q: "Do I have to build everything at once?", a: "No. Most Kostaja engagements start with one or two pieces and ladder into a fuller system over time. The value is the sequence." },
      { q: "Can Kostaja handle brand, website, social, local, and AI in one engagement?", a: "Yes. Kostaja is set up to run all of these as one connected engagement. A single team with commercial judgment owns the strategy and delivery." },
      { q: "How is this different from hiring separate specialists?", a: "Separate specialists often produce excellent parts that do not add up to a system. One team that runs the whole stack produces a system that adds up, at the cost of some specialization." },
    ],
  },
};

export const CORE_ENTITY_DEFINITION = CORE_ENTITY;
